import products from './products.js';
import Storage from './storage.js';
import Table from './table.js';
import events from './event.js';
import Logger from './logger.js';
import PhotoWatchdog from './photoWatchdog.js';

class Presentation {
    customizerContentDiv = null;
    wrapperDiv = null;
    retouchDiv = null;
    options = null;

    constructor(customizerContentDiv, options = null) {
        this.customizerContentDiv = customizerContentDiv;
        this.options = options;
        if (!this.customizerContentDiv) {
            Logger.error('Presentation step failed to load.');
            return;
        }
        this.init();
    }

    async init() {
        await this.createWrapper();
        await this.createTable();

        events.add('reInitUpload', () => {
            this.table.deselectAll();
        });

        // Shop deep link (?paper=..&presentation=..): select the presentation exactly as a click
        // would, without visiting the step. Resolves true when it is stored, false otherwise.
        events.add('applyPresentationPreselect', async (value) => {
            await this.update();
            if (!this.table.clickItemByValue(value)) return false;
            for (let i = 0; i < 40; i++) { // the row click validates asynchronously
                if (Storage.get('presentation') === value) return true;
                await new Promise(r => setTimeout(r, 50));
            }
            return false;
        });
    }

    async createWrapper() {
        if (this.wrapperDiv) {
            // If already initialized, just show the wrapper, handled by CSS
            return;
        }
        this.wrapperDiv = document.createElement('div');
        this.wrapperDiv.classList.add('mscp-presentation-wrapper');
        this.customizerContentDiv.appendChild(this.wrapperDiv);

        // create presentation div
        this.presDiv = document.createElement('div');
        this.presDiv.classList.add('mscp-presentation-list');
        this.wrapperDiv.appendChild(this.presDiv);
    }

    async createTable() {
        // create presentation list
        const presentations = await products.getPresentations();
        this.table = new Table({
            'wrapperDiv': this.presDiv,
            'terms': presentations,
            'type': 'presentation',
            'selectCallback': async (el, value, oldValue) => {
                await events.trigger('updateCropSize', true); /* !this.options.openInEdit); // Ensure cropper is updated after presentation changes if not in edit mode */
            },
            'postCreateTitleCallback': async (term) => {
                const price = document.createElement('span');
                price.classList.add('mscp-term-price', 'mscp-column', 'mscp-post-create');
                const fromPrice = await products.getFromPrice(Storage.get('paper'), term.value);
                price.textContent = fromPrice ? fromPrice : '';
                return price;
            },
        });

        // add event listener for step changes to filter presentations based on selected paper
        events.add('stepChanged', (step) => {
            if (step === 'presentation') {
                this.update();
            }
        });
    }

    async update() {
        // only show the presentations of the selected paper (if paper is selected)
        const paper = Storage.get('paper');
        if (!paper) {
            // No paper selected yet, show all presentations
            const allPresentations = await products.getPresentations();
            this.table.updateVisibleItems(allPresentations.map(p => p.value));
            return;
        }
        const currentPresentations = await products.getAttributeHierarchyPresentations();
        this.table.updateVisibleItems(currentPresentations);

        // Telemetry (issue 3): paper is chosen but no presentations resolved.
        // products.load() now retries, so this should be rare; loaded_ok tells us
        // which case it is — false = the fetch failed all retries (network),
        // true = products loaded but this paper genuinely has no presentations
        // (a data/config issue, not a race). One line per step visit.
        if ((!currentPresentations || currentPresentations.length === 0) && !this._reportedEmpty) {
            this._reportedEmpty = true;
            PhotoWatchdog.report('step_no_options', {
                step: 'presentation',
                paper: String(paper).slice(0, 40),
                loaded_ok: products.loadedOk ? 'yes' : 'no',
                all_count: (await products.getPresentations())?.length ?? -1,
                version: (window.mscp && window.mscp.version) || '',
            });
        }
    }
}

// const presentation = new Presentation();
export default Presentation;